/** How long to treat the room's initial snapshot text as the auto welcome message. */
export const ROOM_WELCOME_PREVIEW_TTL_MS = 60_000;

/** Matches widget auto-welcomes like "Welcome to trustchat". */
export const AUTO_WELCOME_CONTENT_RE = /^welcome to\b/i;

/**
 * Matches the presence notice the backend inserts as a real message when a
 * visitor opens an invited chat, e.g. "Visitor_9876 has joined the chat".
 *
 * Anchored to the whole message so a visitor writing a sentence that happens to
 * contain the phrase is still treated as a normal message.
 */
export const JOIN_NOTICE_CONTENT_RE =
  /^.{0,64}\bhas joined the (chat|conversation|room)\b[.!]?$/i;

const roomWelcomePreviewByRoomId = new Map();

/**
 * @param {string | null | undefined} content
 * @returns {boolean}
 */
export function looksLikeAutoWelcomeContent(content) {
  const text = content != null ? String(content).trim() : "";
  return text.length > 0 && AUTO_WELCOME_CONTENT_RE.test(text);
}

/**
 * @param {string | null | undefined} content
 * @returns {boolean}
 */
export function looksLikeJoinNoticeContent(content) {
  const text = content != null ? String(content).trim() : "";
  return text.length > 0 && JOIN_NOTICE_CONTENT_RE.test(text);
}

/**
 * A join notice is room status, not conversation, so it should not render as a
 * message bubble or raise a notification.
 *
 * @param {Record<string, any> | null | undefined} message
 * @returns {boolean}
 */
export function isJoinNoticeMessage(message) {
  if (!message || typeof message !== "object") return false;
  if (message.isJoinNotice === true) return true;

  const type = typeof message.type === "string" ? message.type.toLowerCase() : "";
  if (type === "join" || type === "user_joined") return true;

  return looksLikeJoinNoticeContent(message.content);
}

/**
 * Remember the welcome text attached to a newly created room so we can skip
 * a duplicate browser notification when the same text arrives via `msg fetched`.
 *
 * @param {string | null | undefined} roomId
 * @param {string | null | undefined} previewContent
 */
/**
 * @param {Record<string, unknown> | null | undefined} room
 */
export function registerRoomWelcomePreviewFromRoom(room) {
  if (!room || typeof room !== "object") return;
  const roomId = room._id;
  if (!roomId) return;

  const snap = room.latestMessageSnapshot;
  if (snap && typeof snap === "object" && snap.content != null) {
    registerRoomWelcomePreview(String(roomId), String(snap.content));
    return;
  }

  const lm = room.latestMessage;
  if (lm && typeof lm === "object" && !Array.isArray(lm) && lm.content != null) {
    registerRoomWelcomePreview(String(roomId), String(lm.content));
  }
}

export function registerRoomWelcomePreview(roomId, previewContent) {
  const content =
    previewContent != null ? String(previewContent).trim() : "";
  if (!roomId || !content) return;
  roomWelcomePreviewByRoomId.set(String(roomId), {
    content,
    expiresAt: Date.now() + ROOM_WELCOME_PREVIEW_TTL_MS,
  });
}

/**
 * @param {string} roomId
 */
function getRoomWelcomePreview(roomId) {
  const entry = roomWelcomePreviewByRoomId.get(String(roomId));
  if (!entry) return null;
  if (Date.now() > entry.expiresAt) {
    roomWelcomePreviewByRoomId.delete(String(roomId));
    return null;
  }
  return entry;
}

/**
 * Returns true when this socket message should not trigger a customer-message
 * browser notification (auto welcome on room creation, system flags, etc.).
 *
 * @param {Record<string, unknown> | null | undefined} message
 * @returns {boolean}
 */
export function shouldSuppressMessageNotification(message) {
  if (!message || typeof message !== "object") return false;

  if (message.isWelcome === true || message.isSystem === true) {
    return true;
  }

  const content =
    message.content != null ? String(message.content).trim() : "";
  if (!content) return false;

  if (looksLikeAutoWelcomeContent(content)) {
    return true;
  }

  if (looksLikeJoinNoticeContent(content)) {
    return true;
  }

  const roomId = message.roomId;
  if (!roomId) return false;

  const preview = getRoomWelcomePreview(roomId);
  if (preview && content === preview.content) {
    return true;
  }

  return false;
}

/**
 * @param {{ title?: string, body?: string, isWelcome?: boolean }} payload
 * @returns {boolean}
 */
export function shouldSuppressPushNotification(payload) {
  if (!payload || typeof payload !== "object") return false;
  if (payload.isWelcome === true) return true;
  return looksLikeAutoWelcomeContent(payload.body);
}

/** @internal Test helper */
export function clearRoomWelcomePreviews() {
  roomWelcomePreviewByRoomId.clear();
}
